import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from "react-router-dom";
import {api} from "../../maps/data.js";
import {addOrDeleteQueryParams, getQueryValue} from "../../domain/queryHandler";
import useZibi from "../../zibi/UseZibi";
import {Breadcrumbs, CircularProgress, Typography} from "@mui/material";
import styled from "styled-components";
import Clients from "../../components/installer/Clients";
import Sites from "../../components/installer/Sites";
import {LoaderStyled} from "../../components/alerts/inventoryManagementAlerts/invCommonStyles";
import SiteData from "../../components/installer/SiteData";

const WrapperStyled = styled.div`
    padding: 20px;
    overflow: auto;
    height: 100%;
`;

export default function MainInstallerDashboardPage() {
    const history = useNavigate();
    const location = useLocation();

    const clientId = getQueryValue(location, 'client');
    const siteId = getQueryValue(location, 'site');
    const edgeId = getQueryValue(location, 'edge');


    const [clients, set_clients] = useZibi('clients');
    const [sitesByClientId, set_sitesByClientId] = useZibi('sitesByClientId');
    const [siteEdges, set_siteEdges] = useZibi('siteEdges');
    const [siteFields, set_siteFields] = useZibi('siteFields');
    const [siteCameras, set_siteCameras] = useZibi('siteCameras');
    const [sensors, set_sensors] = useZibi('sensors');

    const [edges, s_edges] = useState([]);

    const [selected_client, s_selected_client] = useState({});
    const [selected_site, s_selected_site] = useState({});
    const [selected_edge, s_selected_edge] = useState({});
    const [forceFieldUpdate, s_forceFieldUpdate] = useState(0);

    const [loading, s_loading] = useState(true);

    const sites = sitesByClientId?.[clientId] || [];

    const getClients = async () => {
        s_loading(true);
        const res = await api({type: 'getClients'});
        set_clients(res);
        s_loading(false);
    };
    const getSites = async () => {
        if (clientId) {
            s_loading(true);
            const res = await api({
                type: 'getSites',
                data: {
                    clientId: clientId
                }
            });
            const temp = {};
            res.forEach(site => {
                if (!temp[site.clientId]) {
                    temp[site.clientId] = [];
                }
                temp[site.clientId].push(site);
            });
            set_sitesByClientId(temp);
            s_loading(false);
        }
    };
    const getEdges = async () => {
        if (siteId) {
            s_loading(true);
            const res = await api({
                type: 'getEdgesOnly',
                data: {
                    siteId: siteId
                }
            });
            s_edges(res);
            set_siteEdges(res);
            s_loading(false);
        }
    };
    const getSensors = async () => {
        if (siteId) {
            s_loading(true);
            const res = await api({
                type: 'getSiteSensors',
                data: {
                    siteId: siteId
                }
            });
            const temp = {};
            res.forEach(sensor => {
                temp[sensor._id] = sensor;
            });
            set_sensors(temp);
            s_loading(false);
        }
    };

    const getCameras = async () => {
        if (siteId) {
            s_loading(true);
            const res = await api({
                type: 'getSiteCameras',
                data: {
                    siteId: siteId
                }
            });
            const temp = {};
            res.forEach(sensor => {
                temp[sensor._id] = sensor;
            });
            set_siteCameras(temp);
            s_loading(false);
        }
    };

    const getFields = async () => {
        if (siteId) {
            s_loading(true);
            const res = await api({
                type: 'getSiteFields',
                data: {
                    siteId: siteId
                }
            });
            set_siteFields(res);
            s_loading(false);
        }
    };

    useEffect(() => {
        getClients();
    }, []);

    useEffect(() => {
        getSites();
    }, [clientId]);

    useEffect(() => {
        getEdges();
        getFields();
        getSensors();
        getCameras();
    }, [siteId]);

    useEffect(() => {
        if (clients?.length) {
            const client = clients.find(cl => cl._id === clientId);
            if (client) {
                s_selected_client(client);
                s_selected_site({});
                s_selected_edge({});
            }
        }
    }, [clientId, clients]);

    useEffect(() => {
        const isSelectedClientExist = Object.keys(selected_client)?.length;
        if (sites?.length && isSelectedClientExist) {
            const site = sites.find(cl => cl._id === siteId);
            if (site) {
                s_selected_site(site);
                s_selected_edge({});
            }
        }
    }, [siteId, sites, selected_client]);

    useEffect(() => {
        const isSelectedClientExist = Object.keys(selected_client)?.length;
        const isSelectedSiteExist = Object.keys(selected_site)?.length;
        if (edges?.length && isSelectedSiteExist && isSelectedClientExist) {
            const edge = edges.find(cl => cl._id === edgeId);
            if (edge) {
                s_selected_edge(edge);
            }
        }
    }, [edgeId, edges, selected_client, selected_site]);

    const changeUrl = (query) => {
        history({
            search: query
        });
    };

    const breadCrumbs = [{title: 'Clients', id: 'clients'}];
    let currentPage = 'clients';
    if (clientId) {
        currentPage = 'sites';
        const client = clients.find(cl => cl._id === clientId);
        breadCrumbs.push({title: `${client?.name || "Site"}`, id: 'sites'});
    }
    if (siteId) {
        currentPage = 'siteData';
        const site = sites.find(cl => cl._id === siteId);
        breadCrumbs.push({title: `${site?.name || "SiteData"}`, id: 'siteData'});
    }

    return (
        <WrapperStyled>
            <Breadcrumbs aria-label="breadcrumb">
                {breadCrumbs.map(pp => {
                    return (
                        <Typography
                            style={{cursor: 'pointer'}}
                            onClick={() => {
                                let params = [];
                                if (pp.id === 'sites') {
                                    params = [{site: null}];
                                }
                                if (pp.id === 'clients') {
                                    params = [{client: null}, {site: null}];
                                }

                                const urlToPush = addOrDeleteQueryParams(location, params);
                                changeUrl(urlToPush);
                            }}
                            key={pp.id} color="text.primary">{pp.title}</Typography>
                    );
                })}
            </Breadcrumbs>
            {loading && (<LoaderStyled><CircularProgress/></LoaderStyled>)}
            {!loading && (
                <>
                    {currentPage === 'clients' && (
                        <Clients/>
                    )}
                    {currentPage === 'sites' && (
                        <Sites clientId={clientId}/>
                    )}
                    {currentPage === 'siteData' && (
                        <SiteData clientId={clientId} siteId={siteId}/>
                    )}
                </>
            )}
        </WrapperStyled>
    );
}